import { Box, Button, Link, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { routes } from "schemas/routes";

import { trackEvent } from "utils/tracking";

import { Icon } from "./shared/icons";
import { Callout, Pill } from "./shared/primitives";
import { buttonSx, g } from "./shared/tokens";

interface Props {
  completed?: boolean;
}

const AddNicheFollowupCard = ({ completed }: Props) => {
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        background: g.cardBg,
        border: `1px solid ${g.border}`,
        borderRadius: g.radiusLg,
        p: { xs: "16px", md: "22px" },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: "16px",
          flexWrap: "wrap",
          mb: "16px",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: "14px" }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "999px",
              background: completed ? g.green100 : g.primaryTint,
              color: completed ? g.successText : g.primary,
              display: "grid",
              placeItems: "center",
              flexShrink: 0,
            }}
          >
            {completed ? <Icon.Check size={18} /> : <Icon.Send size={18} />}
          </Box>
          <Box>
            <Typography sx={{ fontSize: 16, fontWeight: 800, color: g.text }}>
              Add a followup to your niche
            </Typography>
            <Typography sx={{ fontSize: 13, color: g.text2, mt: "2px" }}>
              Boost reply rates by adding a follow-up template to a niche
            </Typography>
          </Box>
        </Box>
        {completed ? (
          <Pill variant="default" icon={<Icon.Check size={12} />}>
            Completed
          </Pill>
        ) : (
          <Pill variant="yellow" icon={<Icon.Sparkle size={12} />}>
            Recommended
          </Pill>
        )}
      </Box>

      <Callout tone="yellow" icon={<Icon.Sparkle size={16} />}>
        <strong>Why a follow-up?</strong> Most replies come from a second touch.
        Adding a follow-up to a niche's email setting gives your outreach a
        second chance to land — without any extra work from you.
      </Callout>

      <Box sx={{ mt: "18px" }}>
        {completed ? (
          <Typography sx={{ fontSize: 14, color: g.text2 }}>
            <Box
              component="span"
              sx={{
                color: g.successText,
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                mr: "8px",
                fontWeight: 600,
              }}
            >
              <Icon.Check size={14} /> Follow-up added!
            </Box>
            Visit your{" "}
            <Link
              sx={{ fontWeight: 600, textDecoration: "none" }}
              href={`/${routes.virtualAssistant}`}
            >
              VA Settings
            </Link>{" "}
            to manage niches.
          </Typography>
        ) : (
          <Button
            fullWidth
            sx={{ ...buttonSx.base, ...buttonSx.primary }}
            onClick={() => {
              trackEvent("Gamification - Add Niche Followup");
              navigate(`/${routes.virtualAssistant}`);
            }}
          >
            Add a follow-up <Icon.ArrowRight size={14} />
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default AddNicheFollowupCard;
